import type { Client } from '@src/clients/domain/Client';
import type { CheckedFields } from '@src/orders/domain/CheckedFields';
import { getSectionFields } from '@src/orders/domain/CheckedFields';
import type { Order } from '@src/orders/domain/Order';
import { calculateSumOfAxleWeights } from '@src/orders/PermitFormSections/axlesConfiguration/AxleWeightCalculator';
import { axleRowFields } from '@src/orders/schemas/AxleInformationInputsSchema';
import {
  globalTrailerFields,
  trailerFields,
} from '@src/orders/schemas/TrailerInformationInputsSchema';
import { accountFields } from '@src/permitField/AccountFields';
import {
  AxleFormName,
  AxleOverallWeightName,
} from '@src/permitField/AxleInformationFields';
import { commentsFields } from '@src/permitField/CommentsFields';
import { companyAddressFields } from '@src/permitField/CompanyAddressFields';
import { companyInformationFields } from '@src/permitField/CompanyInformationSectionFields';
import { driverInformationFields } from '@src/permitField/DriverInformationFields';
import { insuranceInformationFields } from '@src/permitField/InsuranceInformationFields';
import { jurisdictionIdsFields } from '@src/permitField/JurisdictionIdNumberFields';
import { loadInformationFields } from '@src/permitField/LoadInformationFields';
import { mobileHomeFields } from '@src/permitField/MobileHomeFields';
import {
  overallDimensionsFields,
  OverallWeightField,
} from '@src/permitField/OverallDimensionsFields';
import {
  orderInformationFields,
  permitDeliveryAndNotificationsFields,
  PrimaryDeliveryTypeField,
  PrimaryDeliveryTypeValueField,
  SecondaryDeliveryTypeField,
  SecondaryDeliveryTypeValueField,
} from '@src/permitField/PermitDeliveryAndNotificationsFields';
import { PONumberField } from '@src/permitField/PONumberField';
import { TractorAxlesFormUtils } from '@src/permitField/TractorAxlesFormUtils';
import { tractorDimensionsFields } from '@src/permitField/TractorDimensionsFields';
import { tractorInformationFields } from '@src/permitField/TractorInformationFields';
import { tractorLicenseFields } from '@src/permitField/TractorLicenseFields';
import { tractorOwnerFields } from '@src/permitField/TractorOwnerFields';
import { TrailersFormName } from '@src/permitField/TrailerInformationFields';
import {
  globalTripsFields,
  stateTripFieldNames,
  tripsFields,
  TripsFormName,
} from '@src/permitField/TripInformationFields';
import type { FieldValues } from 'react-hook-form';
import type { AnyObject, Maybe, ObjectSchema } from 'yup';
import * as yup from 'yup';

export const AXLE_WEIGHT_DIFFERENCE_TOLERANCE_BETWEEN_REAL_AND_DISPLAYED_SUM = 5;

const nestedFields = [
  ...Object.keys(tripsFields),
  ...Object.keys(axleRowFields),
  ...Object.keys(trailerFields),
];

type Category = {
  [key: string]: Section;
};

type Schema =
  | yup.Schema<unknown, unknown, unknown, '' | 'd'>
  | ObjectSchema<Maybe<AnyObject>, unknown, unknown, ''>;
type Section = {
  [key: string]: Schema;
};

export const fields: Record<string, Category> = {
  carrierAndPermitDelivery: {
    companyInformation: companyInformationFields,
    companyAddress: companyAddressFields,
    driverInformation: driverInformationFields,
    orderInformation: orderInformationFields,
    permitDeliveryAndNotifications: permitDeliveryAndNotificationsFields,
  },
  loadInformation: {
    loadInformation: loadInformationFields,
    mobileHome: mobileHomeFields,
  },
  haulingEquipment: {
    tractorInformation: tractorInformationFields,
    tractorDimensions: tractorDimensionsFields,
    tractorLicense: tractorLicenseFields,
    tractorOwner: tractorOwnerFields,
    globalTrailers: globalTrailerFields,
    trailers: trailerFields,
  },
  axlesConfiguration: {
    overallDimensions: overallDimensionsFields,
    axles: axleRowFields,
  },
  routingAndStates: {
    globalTrips: globalTripsFields,
    trips: tripsFields,
  },
  accountsAndInsurances: {
    account: accountFields,
    insuranceInformation: insuranceInformationFields,
    juridictionIds: jurisdictionIdsFields,
  },
  notesAndDocuments: {
    comments: commentsFields,
  },
};

const generateFieldsSchema = (
  schema: { [p: string]: Schema },
  checkedFields: string[],
) => {
  const schemaShape = Object.keys(schema).reduce<{
    [key: string]: Schema;
  }>((acc, fieldName) => {
    if (checkedFields.includes(fieldName)) {
      acc[fieldName] = schema[fieldName];
    }
    return acc;
  }, {});

  return yup.object().shape(schemaShape);
};

export class SchemaBuilder {
  static generateTripFieldsSchema(
    apps: { id: string; fields: string[] }[],
  ): Schema {
    const schemaShape = apps.reduce(
      (acc, app) => ({
        ...acc,
        [app.id]: generateFieldsSchema(tripsFields, app.fields),
      }),
      {},
    );

    return yup.object().shape(schemaShape);
  }

  static from(
    formFields: CheckedFields,
    applications: { id: string; fields: string[] }[],
    isPermit: boolean = false,
  ): ObjectSchema<FieldValues> {
    const visibleFields = formFields;
    function mustIncludeTripSectionSchema(checkedFields: string[]) {
      return checkedFields.some((item) => stateTripFieldNames().includes(item));
    }
    let schemaHasAxleField = false;
    let schemaHasTrailerField = false;

    const schemaShape = Object.keys(fields).reduce<{
      [key: string]: Schema;
    }>((acc, category) => {
      const categorySections = fields[category];

      Object.keys(categorySections).forEach((sectionKey) => {
        const sectionFields = categorySections[sectionKey];

        Object.keys(sectionFields).forEach((field) => {
          if (
            field.toLowerCase().includes('axle') &&
            sectionKey === 'axles' &&
            visibleFields.includes(field)
          ) {
            schemaHasAxleField = true;
          }

          if (
            field.toLowerCase().includes('trailer') &&
            visibleFields.includes(field)
          ) {
            schemaHasTrailerField = true;
          }

          if (!nestedFields.includes(field) && visibleFields.includes(field)) {
            acc[field] = sectionFields[field];
          }
        });
      });

      if (mustIncludeTripSectionSchema(visibleFields) && !isPermit) {
        acc[TripsFormName] =
          SchemaBuilder.generateTripFieldsSchema(applications);
      }
      return acc;
    }, {});

    if (schemaHasTrailerField) {
      const trailerDefaultSchema = generateFieldsSchema(
        trailerFields,
        visibleFields,
      );
      if (schemaHasAxleField) {
        const axles = yup
          .array(generateFieldsSchema(axleRowFields, visibleFields))
          .min(1)
          .required();
        schemaShape[TrailersFormName] = yup.array(
          generateFieldsSchema({ ...trailerFields }, visibleFields).concat(
            yup.object({ [AxleFormName]: axles }),
          ),
        );
      } else {
        schemaShape[TrailersFormName] = yup.array(trailerDefaultSchema);
      }
    }

    if (schemaHasAxleField) {
      const axleSchema = yup
        .array(generateFieldsSchema(axleRowFields, visibleFields))
        .min(1)
        .required();
      schemaShape[TractorAxlesFormUtils.fieldName] = yup.object({
        [AxleFormName]: axleSchema,
      });
    }

    return yup.object().shape(schemaShape);
  }
}

export const permitsFormValidations = ({
  permitCheckedFields,
  checkedFields,
  requirePO,
}: {
  permitCheckedFields: { id: string; fields: string[] }[];
  checkedFields: CheckedFields;
  requirePO?: boolean;
}) =>
  yup
    .object()
    .concat(
      SchemaBuilder.from(
        getSectionFields(
          'carrierAndPermitDelivery',
          'driverInformation',
          checkedFields,
        ),
        permitCheckedFields,
      ),
    )

    .concat(SchemaBuilder.from(checkedFields, permitCheckedFields))
    .concat(
      yup.object().shape(
        requirePO
          ? {
              [PONumberField.fieldName]: PONumberField.validator,
            }
          : {},
      ),
    )
    .concat(
      yup.object().shape({
        [AxleOverallWeightName]: yup.lazy((_value, options) => {
          return yup.number().test({
            name: 'isOverallWeightMatchingAxlesWeight',
            message: 'isOverallWeightMatchingAxlesWeight',
            test() {
              if (!checkedFields.includes(OverallWeightField.fieldName)) {
                return true;
              }
              const hasAxleFields = Object.keys(axleRowFields).some((field) =>
                checkedFields.includes(field),
              );
              if (!hasAxleFields) {
                return true;
              }

              const rawFormTractorAxles =
                options.parent[TractorAxlesFormUtils.fieldName];
              const formTractorAxles = rawFormTractorAxles
                ? rawFormTractorAxles[AxleFormName]
                : [];
              const trailers = options.parent[TrailersFormName];
              const sumOfAxleWeight = calculateSumOfAxleWeights(
                trailers,
                formTractorAxles,
              );
              if (
                !this.parent[OverallWeightField.fieldName] ||
                !sumOfAxleWeight
              )
                return true;
              return (
                Math.abs(
                  parseFloat(
                    this.parent[OverallWeightField.fieldName].toFixed(2),
                  ) - parseFloat(sumOfAxleWeight.toFixed(2)),
                ) <=
                AXLE_WEIGHT_DIFFERENCE_TOLERANCE_BETWEEN_REAL_AND_DISPLAYED_SUM
              );
            },
          });
        }),
      }),
    )
    .required();

export const editOrderFormInputs = (
  order: Order,
  checkedFields: CheckedFields,
  client: Client,
) =>
  yup
    .object()
    .concat(
      yup.object().shape({
        [PrimaryDeliveryTypeField.fieldName]:
          PrimaryDeliveryTypeField.validator,
        [PrimaryDeliveryTypeValueField.fieldName]:
          PrimaryDeliveryTypeValueField.validator,
      }),
    )
    .concat(
      yup.object().shape({
        [SecondaryDeliveryTypeField.fieldName]:
          SecondaryDeliveryTypeField.validator.notRequired(),
        [SecondaryDeliveryTypeValueField.fieldName]:
          SecondaryDeliveryTypeValueField.validator,
      }),
    )
    .concat(
      permitsFormValidations({
        requirePO: client.requirePO,
        checkedFields,
        permitCheckedFields: order
          .getAllLatestEffectivePermitApplicationsSortedByUpdatedAt()
          .map((app) => ({
            id: app.application.id,
            fields: app.application.fields,
          })),
      }),
    );

export type EditOrderFormInputs = yup.InferType<
  ReturnType<typeof editOrderFormInputs>
>;
